import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  ArrowBack,
  Assessment,
  CalendarToday,
  TrendingUp,
  TrendingDown,
  Receipt,
  Business,
  GetApp,
  ArrowUpward,
  ArrowDownward,
  UnfoldMore,
  AttachMoney,
} from "@material-ui/icons";
import { useHistory } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { Chart } from "react-google-charts";
import fetchClient from "../../api/fetchClient";
import Loader from "../../components/Loader";
import message from "../../helper/message";
import { formatAbbrValue } from "../../helper/formatValue";
import {
  Container,
  ModernHeader,
  HeaderLeft,
  HeaderContent,
  PageTitle,
  PageSubtitle,
  ResultsCard,
} from "../../components/common";

const PRIMARY = "#e67e22";

const DOC_TYPE_LABELS = {
  "buying-create": "Pedido de Compra — Criação",
  "buying-alter": "Pedido de Compra — Alteração",
  "buying-cancel": "Pedido de Compra — Cancelamento",
  sourcing: "Cotação (Sourcing)",
  "business-partner": "Parceiro de Negócios",
};

const DOC_TYPE_COLORS = ["#e67e22", "#d35400", "#f39c12", "#3498db", "#27ae60", "#8e44ad", "#1abc9c"];
const CURRENCY_COLORS = ["#e67e22", "#3498db", "#27ae60", "#8e44ad", "#e74c3c", "#1abc9c"];

const MONTHS = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
const MONTHS_FULL = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

const YEAR_OPTIONS = [2024, 2025, 2026];

const formatCurrency = (value, currency) => {
  try {
    return new Intl.NumberFormat("pt-BR", { style: "currency", currency: currency || "BRL" }).format(value || 0);
  } catch {
    return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value || 0);
  }
};

const formatAbbrCurrency = (value, currency) => {
  const cur = currency || "BRL";
  const abs = Math.abs(value || 0);
  let abbreviated;
  if (abs >= 1_000_000_000) {
    abbreviated = ((value || 0) / 1_000_000_000).toFixed(1).replace(".", ",") + "B";
  } else if (abs >= 1_000_000) {
    abbreviated = ((value || 0) / 1_000_000).toFixed(1).replace(".", ",") + "M";
  } else if (abs >= 1_000) {
    abbreviated = ((value || 0) / 1_000).toFixed(1).replace(".", ",") + "K";
  } else {
    abbreviated = (value || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  try {
    const symbol = new Intl.NumberFormat("pt-BR", { style: "currency", currency: cur })
      .formatToParts(0)
      .find((p) => p.type === "currency")?.value || cur;
    return `${symbol} ${abbreviated}`;
  } catch {
    return `R$ ${abbreviated}`;
  }
};

function DashboardMetricsAriba() {
  const history = useHistory();

  // Data
  const [groups, setGroups] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [metrics, setMetrics] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);

  // Filters
  const [selectedGroup, setSelectedGroup] = useState("all");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedDocTypes, setSelectedDocTypes] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [selectedCurrency, setSelectedCurrency] = useState("");

  // Chart toggles
  const [donutMode, setDonutMode] = useState("count");
  const [barMode, setBarMode] = useState("count");
  const [separateByCurrency, setSeparateByCurrency] = useState(false);

  // Table
  const [sortCol, setSortCol] = useState("month");
  const [sortDir, setSortDir] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Load groups and companies on mount
  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const { data } = await fetchClient.get("/ariba/getAllGroups");
        setGroups(data || []);
      } catch (e) {
        console.error("Erro ao carregar grupos", e);
      }
    };
    const fetchCompanies = async () => {
      try {
        const { data } = await fetchClient.get("/ariba/getAll");
        setCompanies(data || []);
      } catch (e) {
        console.error("Erro ao carregar empresas", e);
      }
    };
    fetchGroups();
    fetchCompanies();
  }, []);

  // Auto-fetch when group or year changes
  useEffect(() => {
    if (selectedGroup && selectedYear) fetchMetrics();
  }, [selectedGroup, selectedYear]);

  const fetchMetrics = useCallback(async () => {
    if (!selectedGroup) return;
    try {
      setIsLoading(true);
      const params = new URLSearchParams({ grupoId: selectedGroup, year: selectedYear });
      const { data } = await fetchClient.get(`/ariba/metrics?${params}`);
      setMetrics(data || []);
      setHasFetched(true);
      setSelectedCompany("");
      setSelectedDocTypes([]);
      setSelectedCurrency("");
      setSeparateByCurrency(false);
      setCurrentPage(1);
    } catch (e) {
      message.error(e.response?.data?.message || "Erro ao buscar métricas");
      setMetrics([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedGroup, selectedYear]);

  // Unique companies from metrics
  const uniqueCompanies = useMemo(
    () => [...new Set(metrics.map((m) => m.companyCode).filter(Boolean))].sort(),
    [metrics]
  );

  // Unique docTypes from metrics
  const uniqueDocTypes = useMemo(
    () => [...new Set(metrics.map((m) => m.docType).filter(Boolean))].sort(),
    [metrics]
  );

  // Unique currencies from metrics
  const uniqueCurrencies = useMemo(
    () => [...new Set(metrics.map((m) => m.currency).filter(Boolean))].sort(),
    [metrics]
  );

  // Map company_code -> name from /ariba/getAll
  const companyNameMap = useMemo(() => {
    const map = {};
    companies.forEach((c) => {
      if (c.company_code) map[c.company_code] = c.name;
    });
    return map;
  }, [companies]);

  // Toggle docType chip
  const toggleDocType = (dt) => {
    setSelectedDocTypes((prev) =>
      prev.includes(dt) ? prev.filter((d) => d !== dt) : [...prev, dt]
    );
    setCurrentPage(1);
  };

  // Filtered metrics (KPIs, table, donut, bar)
  const filteredMetrics = useMemo(() => {
    let result = metrics;
    if (selectedMonth) result = result.filter((m) => m.month === parseInt(selectedMonth, 10));
    if (selectedDocTypes.length > 0) result = result.filter((m) => selectedDocTypes.includes(m.docType));
    if (selectedCompany) result = result.filter((m) => m.companyCode === selectedCompany);
    if (selectedCurrency) result = result.filter((m) => m.currency === selectedCurrency);
    return result;
  }, [metrics, selectedMonth, selectedDocTypes, selectedCompany, selectedCurrency]);

  // Previous month data (same docType + company + currency filters)
  const prevMetrics = useMemo(() => {
    if (!selectedMonth) return [];
    const prev = parseInt(selectedMonth, 10) - 1;
    if (prev < 1) return [];
    let result = metrics.filter((m) => m.month === prev);
    if (selectedDocTypes.length > 0) result = result.filter((m) => selectedDocTypes.includes(m.docType));
    if (selectedCompany) result = result.filter((m) => m.companyCode === selectedCompany);
    if (selectedCurrency) result = result.filter((m) => m.currency === selectedCurrency);
    return result;
  }, [metrics, selectedMonth, selectedDocTypes, selectedCompany, selectedCurrency]);

  // KPIs grouped by currency — never sum across currencies
  const kpisByCurrency = useMemo(() => {
    const byCurrency = {};
    filteredMetrics.forEach((m) => {
      const curr = m.currency || "BRL";
      if (!byCurrency[curr]) byCurrency[curr] = { transactions: 0, value: 0 };
      byCurrency[curr].transactions += m.transactionCount || 0;
      byCurrency[curr].value += m.totalValue || 0;
    });
    return Object.entries(byCurrency)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([currency, data]) => ({
        currency,
        transactions: data.transactions,
        value: data.value,
        ticket: data.transactions > 0 ? data.value / data.transactions : 0,
      }));
  }, [filteredMetrics]);

  // Prev KPIs by currency (for variation)
  const prevKpisByCurrency = useMemo(() => {
    const byCurrency = {};
    prevMetrics.forEach((m) => {
      const curr = m.currency || "BRL";
      if (!byCurrency[curr]) byCurrency[curr] = { transactions: 0, value: 0 };
      byCurrency[curr].transactions += m.transactionCount || 0;
      byCurrency[curr].value += m.totalValue || 0;
    });
    return byCurrency;
  }, [prevMetrics]);

  // Variation % by currency
  const variationByCurrency = useMemo(() => {
    const pct = (curr, prev) => (prev > 0 ? ((curr - prev) / prev) * 100 : null);
    const result = {};
    kpisByCurrency.forEach((k) => {
      const prev = prevKpisByCurrency[k.currency];
      if (!prev) { result[k.currency] = { transactions: null, value: null, ticket: null }; return; }
      const prevTicket = prev.transactions > 0 ? prev.value / prev.transactions : 0;
      result[k.currency] = {
        transactions: pct(k.transactions, prev.transactions),
        value: pct(k.value, prev.value),
        ticket: pct(k.ticket, prevTicket),
      };
    });
    return result;
  }, [kpisByCurrency, prevKpisByCurrency]);

  // Convenience refs for single-currency KPI display
  const singleKpi = useMemo(
    () => kpisByCurrency[0] || { transactions: 0, value: 0, ticket: 0, currency: "BRL" },
    [kpisByCurrency]
  );
  const singleVariation = useMemo(
    () => variationByCurrency[singleKpi.currency] || { transactions: null, value: null, ticket: null },
    [variationByCurrency, singleKpi]
  );

  // Evolution chart: NOT filtered by month
  const evolutionMetrics = useMemo(() => {
    let result = metrics;
    if (selectedDocTypes.length > 0) result = result.filter((m) => selectedDocTypes.includes(m.docType));
    if (selectedCompany) result = result.filter((m) => m.companyCode === selectedCompany);
    if (selectedCurrency) result = result.filter((m) => m.currency === selectedCurrency);
    return result;
  }, [metrics, selectedDocTypes, selectedCompany, selectedCurrency]);

  // Unique currencies in evolution data (for toggle)
  const uniqueEvolutionCurrencies = useMemo(
    () => [...new Set(evolutionMetrics.map((m) => m.currency).filter(Boolean))].sort(),
    [evolutionMetrics]
  );

  const evolutionChartData = useMemo(() => {
    if (!evolutionMetrics.length) return [["Mês", "Transações", "Valor Total (R$)"]];

    if (separateByCurrency && uniqueEvolutionCurrencies.length > 1) {
      const currencies = uniqueEvolutionCurrencies;
      const grouped = {};
      evolutionMetrics.forEach((m) => {
        const curr = m.currency || "?";
        if (!grouped[m.month]) grouped[m.month] = {};
        if (!grouped[m.month][curr]) grouped[m.month][curr] = 0;
        grouped[m.month][curr] += m.totalValue || 0;
      });
      const chartData = [["Mês", ...currencies]];
      Object.keys(grouped)
        .map(Number)
        .sort((a, b) => a - b)
        .forEach((month) => {
          const row = [MONTHS[month - 1]];
          currencies.forEach((c) => row.push(grouped[month][c] || 0));
          chartData.push(row);
        });
      return chartData;
    }

    const grouped = {};
    evolutionMetrics.forEach((m) => {
      if (!grouped[m.month]) grouped[m.month] = { transactions: 0, value: 0 };
      grouped[m.month].transactions += m.transactionCount || 0;
      grouped[m.month].value += m.totalValue || 0;
    });
    const chartData = [["Mês", "Transações", "Valor Total (R$)"]];
    Object.keys(grouped)
      .map(Number)
      .sort((a, b) => a - b)
      .forEach((month) => chartData.push([MONTHS[month - 1], grouped[month].transactions, grouped[month].value]));
    return chartData;
  }, [evolutionMetrics, separateByCurrency, uniqueEvolutionCurrencies]);

  // Currency used by value-mode charts: explicit selection OR first available
  const chartValueCurrency = useMemo(
    () => selectedCurrency || uniqueCurrencies[0] || "BRL",
    [selectedCurrency, uniqueCurrencies]
  );

  // Donut chart — value mode: label "Tipo (MOEDA)" when multi-currency
  const donutChartData = useMemo(() => {
    if (!filteredMetrics.length) return [["Tipo", "Valor"]];
    const isMultiCurrency = uniqueCurrencies.length > 1;
    if (donutMode === "value") {
      // When multi-currency, group by "Type (CURRENCY)" to show each currency separately
      const grouped = {};
      filteredMetrics.forEach((m) => {
        const typeLabel = DOC_TYPE_LABELS[m.docType] || m.docType || "Outros";
        const curr = m.currency || "BRL";
        const label = isMultiCurrency ? `${typeLabel} (${curr})` : typeLabel;
        if (!grouped[label]) grouped[label] = { value: 0, currency: curr };
        grouped[label].value += m.totalValue || 0;
      });
      const header = isMultiCurrency ? "Valor" : `Valor (${chartValueCurrency})`;
      const chartData = [["Tipo", header, { role: "tooltip" }]];
      Object.entries(grouped)
        .filter(([, d]) => d.value > 0)
        .forEach(([label, d]) => chartData.push([label, d.value, `${label}: ${formatAbbrCurrency(d.value, d.currency)}`]));
      return chartData;
    }
    const grouped = {};
    filteredMetrics.forEach((m) => {
      const typeLabel = DOC_TYPE_LABELS[m.docType] || m.docType || "Outros";
      if (!grouped[typeLabel]) grouped[typeLabel] = 0;
      grouped[typeLabel] += m.transactionCount || 0;
    });
    const chartData = [["Tipo", "Transações"]];
    Object.entries(grouped)
      .filter(([, v]) => v > 0)
      .forEach(([label, v]) => chartData.push([label, v]));
    return chartData;
  }, [filteredMetrics, donutMode, uniqueCurrencies, chartValueCurrency]);

  // Bar chart — value+multi-currency: one series per currency per company
  const barChartData = useMemo(() => {
    if (!filteredMetrics.length) return [["Empresa", "Transações"]];
    const coName = (code) => companyNameMap[code] || code || "N/A";
    const isMultiValue = barMode === "value" && uniqueCurrencies.length > 1;

    if (isMultiValue) {
      const currencies = uniqueCurrencies;
      const grouped = {};
      filteredMetrics.forEach((m) => {
        const co = m.companyCode || "N/A";
        const curr = m.currency || "BRL";
        if (!grouped[co]) grouped[co] = {};
        grouped[co][curr] = (grouped[co][curr] || 0) + (m.totalValue || 0);
      });
      const chartData = [["Empresa", ...currencies]];
      Object.entries(grouped)
        .sort((a, b) => a[0].localeCompare(b[0]))
        .forEach(([co, byCurr]) => chartData.push([coName(co), ...currencies.map(c => byCurr[c] || 0)]));
      return chartData;
    }

    const docTypes = [...new Set(filteredMetrics.map((m) => m.docType))].sort();
    const grouped = {};
    filteredMetrics.forEach((m) => {
      const co = m.companyCode || "N/A";
      if (!grouped[co]) grouped[co] = {};
      if (!grouped[co][m.docType]) grouped[co][m.docType] = { count: 0, value: 0 };
      grouped[co][m.docType].count += m.transactionCount || 0;
      grouped[co][m.docType].value += m.totalValue || 0;
    });
    const headers = ["Empresa", ...docTypes.map((dt) => DOC_TYPE_LABELS[dt] || dt)];
    const chartData = [headers];
    Object.entries(grouped)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .forEach(([co, byType]) => {
        const row = [coName(co)];
        docTypes.forEach((dt) =>
          row.push(barMode === "count" ? byType[dt]?.count || 0 : byType[dt]?.value || 0)
        );
        chartData.push(row);
      });
    return chartData;
  }, [filteredMetrics, barMode, uniqueCurrencies, companyNameMap]);

  // Table sorting
  const handleSort = (col) => {
    if (sortCol === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortCol(col); setSortDir("asc"); }
    setCurrentPage(1);
  };

  const sortedTableData = useMemo(() => {
    return filteredMetrics.slice().sort((a, b) => {
      let va, vb;
      switch (sortCol) {
        case "company": va = a.companyCode || ""; vb = b.companyCode || ""; break;
        case "docType": va = DOC_TYPE_LABELS[a.docType] || a.docType || ""; vb = DOC_TYPE_LABELS[b.docType] || b.docType || ""; break;
        case "currency": va = a.currency || ""; vb = b.currency || ""; break;
        case "month": va = a.month; vb = b.month; break;
        case "transactions": va = a.transactionCount || 0; vb = b.transactionCount || 0; break;
        case "value": va = a.totalValue || 0; vb = b.totalValue || 0; break;
        case "ticket":
          va = a.transactionCount > 0 ? a.totalValue / a.transactionCount : 0;
          vb = b.transactionCount > 0 ? b.totalValue / b.transactionCount : 0;
          break;
        default: va = a.month; vb = b.month;
      }
      if (typeof va === "string") return sortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
      return sortDir === "asc" ? va - vb : vb - va;
    });
  }, [filteredMetrics, sortCol, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sortedTableData.length / rowsPerPage));
  const paginatedData = sortedTableData.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  useEffect(() => { setCurrentPage(1); }, [filteredMetrics, rowsPerPage]);

  // CSV Export — includes Moeda column
  const exportCSV = () => {
    const headers = ["Empresa", "Tipo Doc", "Moeda", "Mês", "Transações", "Valor Total", "Ticket Médio", "Atualização"];
    const rows = sortedTableData.map((m) => {
      const ticket = m.transactionCount > 0 ? m.totalValue / m.transactionCount : 0;
      return [
        m.companyCode || "-",
        DOC_TYPE_LABELS[m.docType] || m.docType || "-",
        m.currency || "-",
        MONTHS_FULL[m.month - 1] || m.month,
        m.transactionCount,
        (m.totalValue || 0).toFixed(2).replace(".", ","),
        ticket.toFixed(2).replace(".", ","),
        m.updatedAt ? new Date(m.updatedAt).toLocaleDateString("pt-BR") : "-",
      ];
    });
    const csv = [headers, ...rows].map((r) => r.join(";")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ariba-metricas-${selectedGroup}-${selectedYear}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const SortIcon = ({ col }) => {
    if (sortCol !== col) return <UnfoldMore style={{ fontSize: 14, opacity: 0.4 }} />;
    return sortDir === "asc"
      ? <ArrowUpward style={{ fontSize: 14, color: PRIMARY }} />
      : <ArrowDownward style={{ fontSize: 14, color: PRIMARY }} />;
  };

  const VariationBadge = ({ pct }) => {
    if (pct === null || pct === undefined) return null;
    const up = pct >= 0;
    return (
      <VarBadge positive={up}>
        {up ? <TrendingUp style={{ fontSize: 12 }} /> : <TrendingDown style={{ fontSize: 12 }} />}
        {up ? "+" : ""}{pct.toFixed(1)}% vs ant.
      </VarBadge>
    );
  };

  const showData = !isLoading && hasFetched;
  const isMultiCurrency = kpisByCurrency.length > 1;

  return (
    <Container>
      <ModernHeader>
        <HeaderLeft>
          <BackBtn onClick={() => history.goBack()}>
            <ArrowBack />
          </BackBtn>
          <HeaderContent>
            <PageTitle>
              <Assessment style={{ marginRight: "12px", fontSize: "32px" }} />
              Dashboard Ariba — Métricas
            </PageTitle>
            <PageSubtitle>Análise de transações por grupo, tipo e empresa</PageSubtitle>
          </HeaderContent>
        </HeaderLeft>
      </ModernHeader>

      <ResultsCard>
        {/* ── Filtros ─────────────────────────── */}
        <FiltersSection>
          <FiltersRow>
            {/* Grupo */}
            <FilterGroup>
              <FilterLabel>
                <Business style={{ fontSize: 15 }} /> Grupo
              </FilterLabel>
              <FSelect value={selectedGroup} onChange={(e) => setSelectedGroup(e.target.value)}>
                <option value="">Selecione um grupo</option>
                <option value="all">Todos os grupos</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>{g.id}</option>
                ))}
              </FSelect>
            </FilterGroup>

            {/* Ano */}
            <FilterGroup>
              <FilterLabel>
                <CalendarToday style={{ fontSize: 15 }} /> Ano
              </FilterLabel>
              <FSelect value={selectedYear} onChange={(e) => setSelectedYear(Number(e.target.value))}>
                {YEAR_OPTIONS.map((y) => <option key={y} value={y}>{y}</option>)}
              </FSelect>
            </FilterGroup>

            {/* Mês */}
            <FilterGroup>
              <FilterLabel>
                <CalendarToday style={{ fontSize: 15 }} /> Mês
              </FilterLabel>
              <FSelect value={selectedMonth} onChange={(e) => { setSelectedMonth(e.target.value); setCurrentPage(1); }}>
                <option value="">Todos os meses</option>
                {MONTHS.map((m, i) => (
                  <option key={i + 1} value={String(i + 1).padStart(2, "0")}>{m}</option>
                ))}
              </FSelect>
            </FilterGroup>

            {/* Empresa */}
            <FilterGroup>
              <FilterLabel>
                <Business style={{ fontSize: 15 }} /> Empresa
              </FilterLabel>
              <FSelect
                value={selectedCompany}
                onChange={(e) => { setSelectedCompany(e.target.value); setCurrentPage(1); }}
                disabled={!hasFetched || uniqueCompanies.length === 0}
              >
                <option value="">Todas</option>
                {uniqueCompanies.map((co) => <option key={co} value={co}>{companyNameMap[co] || co}</option>)}
              </FSelect>
            </FilterGroup>

            {/* Moeda */}
            <FilterGroup>
              <FilterLabel>
                <AttachMoney style={{ fontSize: 15 }} /> Moeda
              </FilterLabel>
              <FSelect
                value={selectedCurrency}
                onChange={(e) => { setSelectedCurrency(e.target.value); setCurrentPage(1); }}
                disabled={!hasFetched || uniqueCurrencies.length === 0}
              >
                <option value="">Todas as moedas</option>
                {uniqueCurrencies.map((c) => <option key={c} value={c}>{c}</option>)}
              </FSelect>
            </FilterGroup>
          </FiltersRow>

          {/* Tipos de Documento (chips) */}
          {hasFetched && uniqueDocTypes.length > 0 && (
            <DocTypeRow>
              <FilterLabel style={{ marginBottom: 0, whiteSpace: "nowrap" }}>
                <Receipt style={{ fontSize: 15 }} /> Tipo de Documento:
              </FilterLabel>
              <ChipsWrap>
                {uniqueDocTypes.map((dt, i) => (
                  <DocChip
                    key={dt}
                    active={selectedDocTypes.includes(dt)}
                    color={DOC_TYPE_COLORS[i % DOC_TYPE_COLORS.length]}
                    onClick={() => toggleDocType(dt)}
                  >
                    {DOC_TYPE_LABELS[dt] || dt}
                  </DocChip>
                ))}
                {selectedDocTypes.length > 0 && (
                  <ClearChip onClick={() => { setSelectedDocTypes([]); setCurrentPage(1); }}>
                    Limpar
                  </ClearChip>
                )}
              </ChipsWrap>
            </DocTypeRow>
          )}
        </FiltersSection>

        {isLoading && <Loader />}

        {!isLoading && !selectedGroup && (
          <EmptyState>
            <Assessment style={{ fontSize: 64, color: "#cbd5e1", marginBottom: 16 }} />
            <EmptyTitle>Selecione um grupo</EmptyTitle>
            <EmptyText>Escolha um grupo Ariba para visualizar as métricas.</EmptyText>
          </EmptyState>
        )}

        {showData && (
          <>
            {/* ── KPIs ──────────────────────────────── */}
            {isMultiCurrency ? (
              /* Múltiplas moedas: um card por moeda */
              <KPIGrid>
                {kpisByCurrency.map((k, idx) => {
                  const varInfo = variationByCurrency[k.currency] || {};
                  const color = CURRENCY_COLORS[idx % CURRENCY_COLORS.length];
                  return (
                    <CurrencyKPICard key={k.currency} color={color}>
                      <CurrencyKPIHeader color={color}>
                        <AttachMoney style={{ fontSize: 16 }} />
                        {k.currency} — {k.transactions.toLocaleString("pt-BR")} transações
                      </CurrencyKPIHeader>
                      <CurrencyKPIValue>{formatAbbrCurrency(k.value, k.currency)}</CurrencyKPIValue>
                      <CurrencyKPIDetail>Ticket médio: {formatAbbrCurrency(k.ticket, k.currency)}</CurrencyKPIDetail>
                      <VariationBadge pct={varInfo.value} />
                    </CurrencyKPICard>
                  );
                })}
              </KPIGrid>
            ) : (
              /* Moeda única: layout clássico de 3 cards */
              <KPIGrid>
                <KPICard color={PRIMARY}>
                  <KPIIcon color={PRIMARY}><Receipt /></KPIIcon>
                  <KPIContent>
                    <KPIValue>{formatAbbrValue(singleKpi.transactions)}</KPIValue>
                    <KPILabel>Total Transações{singleKpi.currency ? ` (${singleKpi.currency})` : ""}</KPILabel>
                    <VariationBadge pct={singleVariation.transactions} />
                  </KPIContent>
                </KPICard>

                <KPICard color="#27ae60">
                  <KPIIcon color="#27ae60"><TrendingUp /></KPIIcon>
                  <KPIContent>
                    <KPIValue>{formatAbbrCurrency(singleKpi.value, singleKpi.currency)}</KPIValue>
                    <KPILabel>Valor Total</KPILabel>
                    <VariationBadge pct={singleVariation.value} />
                  </KPIContent>
                </KPICard>

                <KPICard color="#3498db">
                  <KPIIcon color="#3498db"><Assessment /></KPIIcon>
                  <KPIContent>
                    <KPIValue>{formatAbbrCurrency(singleKpi.ticket, singleKpi.currency)}</KPIValue>
                    <KPILabel>Ticket Médio</KPILabel>
                    <VariationBadge pct={singleVariation.ticket} />
                  </KPIContent>
                </KPICard>
              </KPIGrid>
            )}

            {metrics.length === 0 ? (
              <EmptyState>
                <Assessment style={{ fontSize: 64, color: "#cbd5e1", marginBottom: 16 }} />
                <EmptyTitle>Nenhuma transação encontrada</EmptyTitle>
                <EmptyText>Nenhuma transação encontrada para o período selecionado.</EmptyText>
              </EmptyState>
            ) : (
              <>
                {/* ── Evolução Mensal ─────────────────── */}
                <ChartCard>
                  <ChartCardHeader>
                    <ChartCardTitle>Evolução Mensal — {selectedYear}</ChartCardTitle>
                    {uniqueEvolutionCurrencies.length > 1 && (
                      <ToggleGroup>
                        <ToggleBtn active={!separateByCurrency} onClick={() => setSeparateByCurrency(false)}>Consolidado</ToggleBtn>
                        <ToggleBtn active={separateByCurrency} onClick={() => setSeparateByCurrency(true)}>Por Moeda</ToggleBtn>
                      </ToggleGroup>
                    )}
                  </ChartCardHeader>
                  <Chart
                    chartType={separateByCurrency && uniqueEvolutionCurrencies.length > 1 ? "LineChart" : "ComboChart"}
                    width="100%"
                    height="420px"
                    data={evolutionChartData}
                    options={
                      separateByCurrency && uniqueEvolutionCurrencies.length > 1
                        ? {
                            chartArea: { width: "75%", height: "70%" },
                            hAxis: { title: "Mês" },
                            vAxis: { title: "Valor Total", minValue: 0, format: "short" },
                            colors: CURRENCY_COLORS,
                            legend: { position: "top" },
                            lineWidth: 3,
                            pointSize: 7,
                            animation: { startup: true, duration: 1000, easing: "out" },
                          }
                        : {
                            chartArea: { width: "75%", height: "70%" },
                            hAxis: { title: "Mês" },
                            seriesType: "bars",
                            series: {
                              0: { color: PRIMARY, targetAxisIndex: 0 },
                              1: { type: "line", color: "#27ae60", targetAxisIndex: 1, lineWidth: 3, pointSize: 7 },
                            },
                            vAxes: {
                              0: { title: "Transações", minValue: 0, gridlines: { color: "#f0f0f0" } },
                              1: { title: "Valor Total (R$)", format: "short", gridlines: { color: "#f5f5f5" } },
                            },
                            legend: { position: "top" },
                            animation: { startup: true, duration: 1000, easing: "out" },
                          }
                    }
                  />
                </ChartCard>

                {/* ── Donut + Barras ──────────────────── */}
                <ChartsRow>
                  {donutChartData.length > 1 && (
                    <ChartCard>
                      <ChartCardHeader>
                        <ChartCardTitle>Distribuição por Tipo</ChartCardTitle>
                        <ToggleGroup>
                          <ToggleBtn active={donutMode === "count"} onClick={() => setDonutMode("count")}>Transações</ToggleBtn>
                          <ToggleBtn active={donutMode === "value"} onClick={() => setDonutMode("value")}>Valor</ToggleBtn>
                        </ToggleGroup>
                      </ChartCardHeader>
                      <Chart
                        chartType="PieChart"
                        width="100%"
                        height="340px"
                        data={donutChartData}
                        options={{
                          chartArea: { width: "90%", height: "80%" },
                          colors: DOC_TYPE_COLORS,
                          legend: { position: "bottom", textStyle: { fontSize: 12 } },
                          pieHole: 0.4,
                          sliceVisibilityThreshold: 0,
                          animation: { startup: true, duration: 1000, easing: "out" },
                        }}
                      />
                    </ChartCard>
                  )}

                  {barChartData.length > 1 && (
                    <ChartCard>
                      <ChartCardHeader>
                        <ChartCardTitle>Comparativo por Empresa</ChartCardTitle>
                        <ToggleGroup>
                          <ToggleBtn active={barMode === "count"} onClick={() => setBarMode("count")}>Transações</ToggleBtn>
                          <ToggleBtn active={barMode === "value"} onClick={() => setBarMode("value")}>Valor</ToggleBtn>
                        </ToggleGroup>
                      </ChartCardHeader>
                      <Chart
                        chartType="ColumnChart"
                        width="100%"
                        height="340px"
                        data={barChartData}
                        options={{
                          chartArea: { width: "75%", height: "65%" },
                          hAxis: { title: "Empresa" },
                          vAxis: { title: barMode === "count" ? "Transações" : (uniqueCurrencies.length > 1 ? "Valor" : `Valor (${chartValueCurrency})`), minValue: 0, ...(barMode === "value" ? { format: "short" } : {}) },
                          colors: DOC_TYPE_COLORS,
                          isStacked: false,
                          legend: { position: "top", textStyle: { fontSize: 11 } },
                          animation: { startup: true, duration: 1000, easing: "out" },
                        }}
                      />
                    </ChartCard>
                  )}
                </ChartsRow>

                {/* ── Tabela ──────────────────────────── */}
                <TableCard>
                  <TableCardHeader>
                    <TableTitle>Detalhamento</TableTitle>
                    <ExportBtn onClick={exportCSV} disabled={sortedTableData.length === 0}>
                      <GetApp style={{ fontSize: 17 }} /> Exportar CSV
                    </ExportBtn>
                  </TableCardHeader>
                  <TableWrap>
                    <DataTable>
                      <thead>
                        <tr>
                          <SortHeader onClick={() => handleSort("company")}>
                            Empresa <SortIcon col="company" />
                          </SortHeader>
                          <SortHeader onClick={() => handleSort("docType")}>
                            Tipo Doc <SortIcon col="docType" />
                          </SortHeader>
                          <SortHeader onClick={() => handleSort("currency")}>
                            Moeda <SortIcon col="currency" />
                          </SortHeader>
                          <SortHeader onClick={() => handleSort("month")}>
                            Mês <SortIcon col="month" />
                          </SortHeader>
                          <SortHeader align="right" onClick={() => handleSort("transactions")}>
                            Transações <SortIcon col="transactions" />
                          </SortHeader>
                          <SortHeader align="right" onClick={() => handleSort("value")}>
                            Valor Total <SortIcon col="value" />
                          </SortHeader>
                          <SortHeader align="right" onClick={() => handleSort("ticket")}>
                            Ticket Médio <SortIcon col="ticket" />
                          </SortHeader>
                          <TH align="right">Atualização</TH>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedData.length === 0 ? (
                          <tr>
                            <TD colSpan={8} style={{ textAlign: "center", padding: "32px", color: "#9ca3af" }}>
                              Nenhuma transação encontrada para o período selecionado.
                            </TD>
                          </tr>
                        ) : (
                          paginatedData.map((m, idx) => {
                            const ticket = m.transactionCount > 0 ? m.totalValue / m.transactionCount : 0;
                            const curr = m.currency || "BRL";
                            return (
                              <TRow key={idx}>
                                <TD><strong>{companyNameMap[m.companyCode] || m.companyCode || "-"}</strong></TD>
                                <TD>
                                  <DocTypeBadge>{DOC_TYPE_LABELS[m.docType] || m.docType || "-"}</DocTypeBadge>
                                </TD>
                                <TD>
                                  <CurrencyBadge>{curr}</CurrencyBadge>
                                </TD>
                                <TD>{MONTHS_FULL[m.month - 1] || "-"}</TD>
                                <TD align="right">
                                  <Metric color={PRIMARY}>{(m.transactionCount || 0).toLocaleString("pt-BR")}</Metric>
                                </TD>
                                <TD align="right">
                                  <Metric color="#27ae60">{formatCurrency(m.totalValue, curr)}</Metric>
                                </TD>
                                <TD align="right">
                                  <Metric color="#3498db">{formatCurrency(ticket, curr)}</Metric>
                                </TD>
                                <TD align="right" style={{ color: "#6b7280", fontSize: 13 }}>
                                  {m.updatedAt ? new Date(m.updatedAt).toLocaleDateString("pt-BR") : "-"}
                                </TD>
                              </TRow>
                            );
                          })
                        )}
                      </tbody>
                    </DataTable>
                  </TableWrap>

                  <PaginationContainer>
                    <PaginationInfo>
                      Mostrando {sortedTableData.length === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1}–
                      {Math.min(currentPage * rowsPerPage, sortedTableData.length)} de {sortedTableData.length} registros
                    </PaginationInfo>
                    <PaginationControls>
                      <PaginationLabel>Linhas por página:</PaginationLabel>
                      <PaginationSelect value={rowsPerPage} onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                      </PaginationSelect>
                      <PageBtn disabled={currentPage === 1} onClick={() => setCurrentPage((p) => p - 1)}>Anterior</PageBtn>
                      <PageInfo>Página {currentPage} de {totalPages}</PageInfo>
                      <PageBtn disabled={currentPage >= totalPages} onClick={() => setCurrentPage((p) => p + 1)}>Próxima</PageBtn>
                    </PaginationControls>
                  </PaginationContainer>
                </TableCard>
              </>
            )}
          </>
        )}
      </ResultsCard>
    </Container>
  );
}

export default DashboardMetricsAriba;

// ===== STYLED COMPONENTS =====

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const BackBtn = styled.button`
  background: rgba(230,126,34,0.1);
  border: none;
  border-radius: 12px;
  width: 42px; height: 42px;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer;
  color: ${PRIMARY};
  transition: all 0.3s ease;
  flex-shrink: 0;
  &:hover { background: rgba(230,126,34,0.2); transform: translateX(-4px); }
`;

// Filters
const FiltersSection = styled.div`
  padding: 24px 0;
  margin-bottom: 24px;
  border-bottom: 1px solid #f0f0f0;
`;

const FiltersRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
`;

const DocTypeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FilterLabel = styled.label`
  font-size: 12px;
  font-weight: 700;
  color: #64748b;
  font-family: "Mulish", sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  display: flex;
  align-items: center;
  gap: 5px;
`;

const FSelect = styled.select`
  width: 100%;
  padding: 10px 14px;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  font-size: 14px;
  font-family: "Mulish", sans-serif;
  background: #f9fafb;
  color: #2c3e50;
  cursor: pointer;
  transition: all 0.2s ease;
  appearance: none;
  &:focus { outline: none; border-color: ${PRIMARY}; box-shadow: 0 0 0 3px rgba(230,126,34,0.1); background: white; }
  &:hover { border-color: #d0d0d0; background: white; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const ChipsWrap = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const DocChip = styled.button`
  border: 2px solid ${(p) => (p.active ? p.color : "#e5e7eb")};
  border-radius: 8px;
  padding: 6px 14px;
  font-size: 12px;
  font-weight: 600;
  font-family: "Mulish", sans-serif;
  background: ${(p) => (p.active ? `${p.color}15` : "#f9fafb")};
  color: ${(p) => (p.active ? p.color : "#9ca3af")};
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover { border-color: ${(p) => p.color}; color: ${(p) => p.color}; background: ${(p) => `${p.color}10`}; }
`;

const ClearChip = styled.button`
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  font-family: "Mulish", sans-serif;
  background: white;
  color: #6b7280;
  cursor: pointer;
  &:hover { background: #f3f4f6; color: #374151; }
`;

// KPIs
const KPIGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
  margin-bottom: 24px;
`;

const KPICard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  border: 1px solid #e8e8e8;
  display: flex;
  align-items: flex-start;
  gap: 16px;
  animation: ${fadeIn} 0.5s ease;
  transition: all 0.3s ease;
  &:hover { transform: translateY(-4px); box-shadow: 0 8px 20px rgba(0,0,0,0.1); }
`;

const KPIIcon = styled.div`
  width: 56px; height: 56px;
  border-radius: 12px;
  background: ${(p) => `${p.color}15`};
  color: ${(p) => p.color};
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  svg { font-size: 28px; }
`;

const KPIContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const KPIValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #2c3e50;
  font-family: "Mulish", sans-serif;
`;

const KPILabel = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
  font-family: "Mulish", sans-serif;
`;

// Multi-currency KPI card
const CurrencyKPICard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 20px 24px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  border: 1px solid #e8e8e8;
  border-left: 4px solid ${(p) => p.color};
  animation: ${fadeIn} 0.5s ease;
  transition: all 0.3s ease;
  &:hover { transform: translateY(-4px); box-shadow: 0 8px 20px rgba(0,0,0,0.1); }
`;

const CurrencyKPIHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 13px;
  font-weight: 700;
  color: ${(p) => p.color};
  font-family: "Mulish", sans-serif;
  margin-bottom: 10px;
`;

const CurrencyKPIValue = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: #2c3e50;
  font-family: "Mulish", sans-serif;
  margin-bottom: 4px;
`;

const CurrencyKPIDetail = styled.div`
  font-size: 12px;
  color: #6b7280;
  font-family: "Mulish", sans-serif;
  margin-bottom: 6px;
`;

const VarBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-top: 4px;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  font-family: "Mulish", sans-serif;
  background: ${(p) => p.positive ? "#27ae6015" : "#e74c3c15"};
  color: ${(p) => p.positive ? "#27ae60" : "#e74c3c"};
`;

// Charts
const ChartsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 24px;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
  & > div { margin-bottom: 0; }
`;

const ChartCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  border: 1px solid #e8e8e8;
  margin-bottom: 24px;
  animation: ${fadeIn} 0.5s ease;
`;

const ChartCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const ChartCardTitle = styled.h4`
  font-size: 15px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
  font-family: "Mulish", sans-serif;
`;

const ToggleGroup = styled.div`
  display: flex;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
`;

const ToggleBtn = styled.button`
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 600;
  font-family: "Mulish", sans-serif;
  border: none;
  cursor: pointer;
  background: ${(p) => p.active ? PRIMARY : "white"};
  color: ${(p) => p.active ? "white" : "#6b7280"};
  transition: all 0.2s ease;
  &:hover { background: ${(p) => p.active ? PRIMARY : "#f9fafb"}; }
`;

// Table
const TableCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  border: 1px solid #e8e8e8;
  animation: ${fadeIn} 0.5s ease;
`;

const TableCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const TableTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
  font-family: "Mulish", sans-serif;
`;

const ExportBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: 2px solid ${PRIMARY};
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  font-family: "Mulish", sans-serif;
  background: white;
  color: ${PRIMARY};
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover:not(:disabled) { background: ${PRIMARY}; color: white; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const TableWrap = styled.div`
  overflow-x: auto;
`;

const DataTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const SortHeader = styled.th`
  text-align: ${(p) => p.align || "left"};
  padding: 12px 16px;
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid #e5e7eb;
  font-family: "Mulish", sans-serif;
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
  &:hover { color: ${PRIMARY}; }
`;

const TH = styled.th`
  text-align: ${(p) => p.align || "left"};
  padding: 12px 16px;
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid #e5e7eb;
  font-family: "Mulish", sans-serif;
`;

const TRow = styled.tr`
  border-bottom: 1px solid #e5e7eb;
  transition: background 0.2s ease;
  &:last-child { border-bottom: none; }
  &:hover { background: #fef9f5; }
`;

const TD = styled.td`
  text-align: ${(p) => p.align || "left"};
  padding: 14px 16px;
  font-size: 14px;
  color: #2c3e50;
  font-family: "Mulish", sans-serif;
`;

const Metric = styled.span`
  display: inline-block;
  padding: 5px 10px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  background: ${(p) => `${p.color}15`};
  color: ${(p) => p.color};
`;

const DocTypeBadge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  background: rgba(230,126,34,0.1);
  color: ${PRIMARY};
`;

const CurrencyBadge = styled.span`
  display: inline-block;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
  background: rgba(52,152,219,0.1);
  color: #3498db;
  font-family: "Mulish", sans-serif;
  letter-spacing: 0.04em;
`;

// Pagination
const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0 0;
  margin-top: 16px;
  border-top: 1px solid #e5e7eb;
  flex-wrap: wrap;
  gap: 12px;
`;

const PaginationInfo = styled.span`
  font-size: 13px;
  color: #6b7280;
  font-family: "Mulish", sans-serif;
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const PaginationLabel = styled.span`
  font-size: 13px;
  color: #6b7280;
  font-family: "Mulish", sans-serif;
`;

const PaginationSelect = styled.select`
  padding: 6px 10px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 13px;
  font-family: "Mulish", sans-serif;
  background: white;
  cursor: pointer;
  &:focus { outline: none; border-color: ${PRIMARY}; }
`;

const PageBtn = styled.button`
  padding: 7px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 13px;
  font-family: "Mulish", sans-serif;
  background: white;
  color: #2c3e50;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover:not(:disabled) { background: #f3f4f6; border-color: ${PRIMARY}; color: ${PRIMARY}; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const PageInfo = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #2c3e50;
  font-family: "Mulish", sans-serif;
`;

// Empty
const EmptyState = styled.div`
  text-align: center;
  padding: 64px 24px;
  animation: ${fadeIn} 0.5s ease;
`;

const EmptyTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0 0 12px 0;
  font-family: "Mulish", sans-serif;
`;

const EmptyText = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0;
  font-family: "Mulish", sans-serif;
`;
